import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import { Transition } from 'Shared/Transition';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import http from 'Utils/http';
import config from '../../../../config';
import { copyText } from 'Utils/clipboard';

const AiPromptDialog = ({ visible, setVisible }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [task, setTask] = useState('');
    const [generatedPrompt, setGeneratedPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const hiddenTextareaRef = useRef(null);

    const writePromptToHiddenTextarea = (prompt) => {
        const textArea = hiddenTextareaRef.current;
        if (textArea && prompt) {
            textArea.value = prompt;
        }
    };

    const onClose = () => {
        setTask('');
        setGeneratedPrompt('');
        setVisible(false);
    };

    const handleGeneratePrompt = async () => {
        if (!task.trim()) {
            enqueueSnackbar('Please enter a task description', {
                action: CustomSnackbar,
                variant: 'error'
            });
            return;
        }

        setIsLoading(true);

        try {
            const response = await http.post('/ai/generate-flow-prompt', {
                task: task
            });

            setGeneratedPrompt(response.data.prompt);
            writePromptToHiddenTextarea(response.data.prompt);
            enqueueSnackbar('Prompt generated successfully!', {
                action: CustomSnackbar,
                variant: 'success'
            });
        } catch (error) {
            enqueueSnackbar(
                error.response?.data?.error || 'Failed to generate prompt',
                {
                    action: CustomSnackbar,
                    variant: 'error'
                }
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyPrompt = () => {
        const textArea = hiddenTextareaRef.current;
        if (!textArea || !textArea.value) {
            enqueueSnackbar('Nothing to copy', {
                action: CustomSnackbar,
                variant: 'error'
            });
            return;
        }

        copyText(textArea.value)
            .then(() => {
                enqueueSnackbar('Prompt copied! Paste it into ChatGPT or Claude.', {
                    action: CustomSnackbar,
                    variant: 'success'
                });
            })
            .catch(() => {
                enqueueSnackbar('Failed to copy', {
                    action: CustomSnackbar,
                    variant: 'error'
                });
            });
    };

    return (
        <>
        <Dialog
            open={visible}
            TransitionComponent={Transition}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: '8px'
            }}>
                <Box component="span" sx={{ color: '#000', fontWeight: 600, fontSize: '24px' }}>
                    Generate Flow Prompt
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={onClose} variant="outlined" size="small">
                        Close
                    </Button>
                    {generatedPrompt ? (
                        <Button
                            onClick={handleGeneratePrompt}
                            variant="outlined"
                            size="small"
                            disabled={isLoading || !task.trim()}
                        >
                            {isLoading ? 'Generating...' : 'Regenerate'}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleGeneratePrompt}
                            variant="contained"
                            size="small"
                            disabled={isLoading || !task.trim()}
                        >
                            {isLoading ? 'Generating...' : 'Generate Prompt'}
                        </Button>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label="Describe your task"
                        multiline
                        rows={6}
                        fullWidth
                        value={task}
                        onChange={(e) => setTask(e.target.value)}
                        placeholder="Example: Get list of users from API https://api.example.com/users, for each user get their orders from https://api.example.com/orders/{user_id}, combine the data and return an array with user name and order count"
                        helperText="Describe what your flow should do, which APIs to call, and what data to return"
                    />
                </Box>

                {generatedPrompt && (
                    <Box sx={{ mt: 3 }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1
                        }}>
                            <strong>Generated Prompt:</strong>
                            {config.AI_ENABLED && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={handleCopyPrompt}
                                >
                                    Copy for AI
                                </Button>
                            )}
                        </Box>
                        <TextField
                            multiline
                            rows={12}
                            fullWidth
                            value={generatedPrompt}
                            InputProps={{
                                readOnly: true,
                                style: { fontFamily: 'monospace', fontSize: '12px' }
                            }}
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
        <textarea
            ref={hiddenTextareaRef}
            style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
            tabIndex={-1}
            aria-hidden="true"
        />
        </>
    );
};

export default AiPromptDialog;
